import {
  Box,
  Button,
  Column,
  Columns,
  DropdownButton,
  DropdownButtonContext,
  EmptyState,
  FlexBox,
  IconButton,
  List,
  Spinner,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { EmailDraftT } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import moment from 'moment-timezone';
import { memo, useCallback, useMemo } from 'react';
import { useMount } from 'react-use';

import { appActions } from 'app/app.actions';
import { AppLink } from 'components/appLink';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { composeEmailActions } from 'components/shared/composeEmail/actions/composeEmailActions';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import ComposeEmailHelper from '../../../shared/composeEmail/helper/ComposeEmailHelper';
import { TopbarButton } from '../topbarButton/';

import styles from './emailTopbarButton.module.scss';

const numberOfVisibleDrafts = 5;

type EmailTopbarButtonProps = {
  alignCenter?: boolean;
};

export const EmailTopbarButton = memo(function EmailTopbarButton({
  alignCenter,
}: EmailTopbarButtonProps): JSX.Element {
  const dispatch = useAppDispatch();
  const flags = useFeatureFlag(['email-compose-v2']);

  const { emailDrafts, meta } = useAppSelector((state) => state.composeEmail);

  useMount(() => {
    dispatch(composeEmailActions.composeEmailGetDrafts());
    dispatch(composeEmailActions.composeEmailGetAllFrom());
  });

  const onToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        dispatch(composeEmailActions.composeEmailGetDrafts());
        ampli.topbarEmailOpened();
      }
    },
    [dispatch],
  );

  const openCompose = useCallback(
    (draft?: EmailDraftT) => {
      if (flags.emailComposeV2) {
        dispatch(
          appActions.showModal({
            modal: {
              type: 'app:composeEmail',
              arg: draft ? { draft } : {},
            },
          }),
        );
      } else {
        ComposeEmailHelper.focus();
        dispatch(
          composeEmailActions.composeEmailOpenModal(
            draft ? { initialValues: draft } : undefined,
          ),
        );
      }
    },
    [dispatch, flags],
  );

  const tooltip = (
    <CommandKeybindingsTooltipContent commandName="conversation:composeNewEmail">
      New e-mail
    </CommandKeybindingsTooltipContent>
  );

  const showNotificationIndicator = emailDrafts.length > 0;
  const isFetching = meta.getDrafts === 'loading' && !emailDrafts.length;

  const visibleEmailDrafts = useMemo(
    (): EmailDraftT[] => emailDrafts.slice(-numberOfVisibleDrafts).reverse(),
    [emailDrafts],
  );

  const notVisibleDraftsNumber = emailDrafts.length - visibleEmailDrafts.length;

  return (
    <DropdownButton
      icon="bell"
      className={styles.root}
      onToggle={onToggle}
      noArrow
      noStyle
      dropdown={
        <DropdownButtonContext.Consumer>
          {({ closeDropdown }) => (
            <Columns>
              <Column
                className={classnames(
                  {
                    [styles.fullWidthContent]: alignCenter,
                  },
                  styles.content,
                )}
              >
                <Column.Row slottable borderBottom>
                  <Column.Slot placement="start">
                    <Text.Heading>Email</Text.Heading>
                  </Column.Slot>

                  <Column.Slot placement="end">
                    <Tooltip content={tooltip}>
                      <Button
                        type="button"
                        variant="secondary"
                        style={{ width: '100%' }}
                        onClick={() => openCompose()}
                      >
                        Compose
                      </Button>
                    </Tooltip>
                  </Column.Slot>
                </Column.Row>

                <Column.Row
                  scrollable
                  noHorizontalPadding
                  noVerticalPadding={visibleEmailDrafts.length > 0}
                >
                  {visibleEmailDrafts.length ? (
                    <Box padding="200 0">
                      <List className={styles.list}>
                        {visibleEmailDrafts.map((draft, index) => (
                          <List.Item
                            key={index}
                            withHoverEffect={false}
                            title={
                              <FlexBox
                                flexDirection="column"
                                padding="0 0 0 250"
                              >
                                <Text.Paragraph
                                  variant="2"
                                  as="em"
                                  color="secondary"
                                >
                                  {draft.subject || 'Unknown'} (Unsent draft)
                                </Text.Paragraph>

                                <Text.Paragraph
                                  as="div"
                                  variant="4"
                                  color="tertiary"
                                >
                                  {moment(draft.timestamp).fromNow()}
                                </Text.Paragraph>
                              </FlexBox>
                            }
                            contentRight={
                              <FlexBox>
                                <Tooltip content="Edit">
                                  <IconButton
                                    icon="pen"
                                    variant="tertiary"
                                    onClick={() => openCompose(draft)}
                                  />
                                </Tooltip>

                                <Tooltip content="Delete">
                                  <IconButton
                                    icon="trash"
                                    variant="tertiary"
                                    onClick={() =>
                                      dispatch(
                                        composeEmailActions.composeEmailRemoveDraft(
                                          {
                                            timestamp: draft.timestamp,
                                          },
                                        ),
                                      )
                                    }
                                  />
                                </Tooltip>
                              </FlexBox>
                            }
                          />
                        ))}
                      </List>
                    </Box>
                  ) : isFetching ? (
                    <FlexBox
                      height="100%"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Spinner size={32} />
                    </FlexBox>
                  ) : (
                    <EmptyState
                      title="No email drafts"
                      description="You haven't saved any email drafts yet."
                      icon={null}
                    />
                  )}
                </Column.Row>

                <Column.Row slottable noMinHeight borderTop>
                  <Column.Slot placement="center">
                    <AppLink path="/drafts" onClick={closeDropdown}>
                      {notVisibleDraftsNumber > 0
                        ? `See all drafts (${notVisibleDraftsNumber} more)`
                        : 'See all drafts'}
                    </AppLink>
                  </Column.Slot>
                </Column.Row>
              </Column>
            </Columns>
          )}
        </DropdownButtonContext.Consumer>
      }
      dropdownProps={{
        className: classnames({
          [styles.dropdownMenuAlignCenter]: alignCenter,
        }),
      }}
      data-testid="compose-email-dropdown"
    >
      <TopbarButton
        icon="mail"
        tooltip={tooltip}
        notificationIndicatorTooltip={
          showNotificationIndicator ? 'You have unused email drafts' : undefined
        }
        notificationIndicatorClassName={styles.notificationIndicator}
        showNotificationIndicator={showNotificationIndicator}
      />
    </DropdownButton>
  );
});
